const { mavlink20 } = require('../mavlink/mavlink');
const MavlinkFTP = require('../mavlink/mavlink_ftp');

const COPTER_MODES = {
    0: "Stabilize",       // manual airframe angle with manual throttle
    1: "Acro",            // manual body-frame angular rate with manual throttle
    2: "Alt Hold",        // manual airframe angle with automatic throttle
    3: "Auto",            // fully automatic waypoint control using mission commands
    4: "Guided",          // fully automatic fly to coordinate or fly at velocity/direction using GCS immediate commands
    5: "Loiter",          // automatic horizontal acceleration with automatic throttle
    6: "RTL",             // automatic return to launching point
    7: "Circle",          // automatic circular flight with automatic throttle
    9: "Land",            // automatic landing with horizontal position control
    11: "Drift",          // semi-autonomous position, yaw and throttle control
    13: "Sport",          // manual earth-frame angular rate control with manual throttle
    14: "Flip",           // automatically flip the vehicle on the roll axis
    15: "Autotune",       // automatically tune the vehicle's roll and pitch gains
    16: "PosHold",        // automatic position hold with manual override, with automatic throttle
    17: "Brake",          // full-brake using inertial/GPS system, no pilot input
    18: "Throw",          // throw to launch mode using inertial/GPS system, no pilot input
    19: "Avoid ADSB",     // automatic avoidance of obstacles in the macro scale - e.g. full-sized aircraft
    20: "Guided No GPS",  // guided mode but only accepts attitude and altitude
    21: "Smart RTL",      // SMART_RTL returns to home by retracing its steps
    22: "FlowHold",       // FLOWHOLD holds position with optical flow without rangefinder
    23: "Follow",         // follow attempts to follow another vehicle or ground station
    24: "ZigZag",         // ZIGZAG mode is able to fly in a zigzag manner with predefined point A and point B
    25: "System ID",      // System ID mode produces automated system identification signals in the controllers
    26: "Autorotate",     // Autonomous autorotation
    27: "Auto RTL",       // Auto RTL, this is not a true mode, AUTO will report as this mode if entered to perform a DO_LAND_START Landing sequence
    28: "Turtle"          // Flip over after crash
};

const PLANE_MODES = {
    // Mirrors MAVProxy's mode_mapping_apm: Plane firmware advertises every
    // mode (including all Q* modes) regardless of whether the build has
    // QuadPlane enabled, so we expose the full list and let the FC reject
    // unsupported ones at switch time.
    0: "Manual",
    1: "Circle",
    2: "Stabilize",
    3: "Training",
    4: "Acro",
    5: "FBWA",
    6: "FBWB",
    7: "Cruise",
    8: "Autotune",
    10: "Auto",
    11: "RTL",
    12: "Loiter",
    13: "Takeoff",
    14: "Avoid ADSB",
    15: "Guided",
    16: "Initialising",
    17: "QStabilize",
    18: "QHover",
    19: "QLoiter",
    20: "QLand",
    21: "QRTL",
    22: "QAutotune",
    23: "QAcro",
    24: "Thermal",
    25: "Loiter Alt QLand",
    26: "Autoland"
};

// Kept as an alias so existing references keep working; Plane firmware
// reports MAV_TYPE_FIXED_WING even on QuadPlane builds, so both map to
// the same full mode list above.
const QUADPLANE_MODES = PLANE_MODES;

const ROVER_MODES = {
    0: "Manual",
    1: "Acro",
    3: "Steering",
    4: "Hold",
    5: "Loiter",
    6: "Follow",
    7: "Simple",
    8: "Dock",
    9: "Circle",
    10: "Auto",
    11: "RTL",
    12: "Smart RTL",
    15: "Guided",
    16: "Initialising"
};

const SUB_MODES = {
    0: "Stabilize",
    1: "Acro",
    2: "Alt Hold",
    3: "Auto",
    4: "Guided",
    7: "Circle",
    9: "Surface",
    16: "PosHold",
    19: "Manual",
    20: "Motor Detect",
    21: "SurfTrak"
};

const TRACKER_MODES = {
    0: "Manual",
    1: "Stop",
    2: "Scan",
    3: "ServoTest",
    10: "Auto",
    16: "Initialising"
};

class Vehicle {
    constructor(mavType, mavId, componentId, sendMavlinkMsg, sendToMainWindow) {
        this.mavType = mavType;
        this.mavId = mavId;
        this.componentId = componentId;
        this.params = {};
        this.sendMavlinkMsg = sendMavlinkMsg;
        this.sendToMainWindow = sendToMainWindow;
        this.mavlinkFTP = new MavlinkFTP(this.mavId, this.componentId, this.sendMavlinkMsg.bind(this), this.sendToMainWindow);
    };

    storeParam(param_id, value, type, metaData) {
        this.params[param_id] = {value: value, type: type, metaData: metaData}; 
        return this.params[param_id];
    };

    getMavTypeName() {
        switch (this.mavType) {
            case mavlink20.MAV_TYPE_FIXED_WING:
            case mavlink20.MAV_TYPE_FLAPPING_WING:
                return "Plane";
            case mavlink20.MAV_TYPE_QUADROTOR:
            case mavlink20.MAV_TYPE_COAXIAL:
            case mavlink20.MAV_TYPE_HELICOPTER:
            case mavlink20.MAV_TYPE_HEXAROTOR:
            case mavlink20.MAV_TYPE_OCTOROTOR:
            case mavlink20.MAV_TYPE_TRICOPTER:
            case mavlink20.MAV_TYPE_DODECAROTOR:
                return "Copter";
            case mavlink20.MAV_TYPE_GROUND_ROVER:
            case mavlink20.MAV_TYPE_SURFACE_BOAT:
                return "Rover";
            case mavlink20.MAV_TYPE_SUBMARINE:
                return "Sub";
            case mavlink20.MAV_TYPE_ANTENNA_TRACKER:
                return "Tracker";
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_DUOROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_QUADROTOR:
            case mavlink20.MAV_TYPE_VTOL_TILTROTOR:
            case mavlink20.MAV_TYPE_VTOL_FIXEDROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER:
            case mavlink20.MAV_TYPE_VTOL_TILTWING:
            case mavlink20.MAV_TYPE_VTOL_RESERVED5:
            case mavlink20.MAV_TYPE_VTOL_RESERVED6:
                return "QuadPlane";
            default:
                return "Unknown";
        }
    };

    getMavTypeTargetName() {
        switch (this.mavType) {
            case mavlink20.MAV_TYPE_FIXED_WING:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_DUOROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_QUADROTOR:
            case mavlink20.MAV_TYPE_VTOL_TILTROTOR:
            case mavlink20.MAV_TYPE_VTOL_FIXEDROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER:
            case mavlink20.MAV_TYPE_VTOL_TILTWING:
            case mavlink20.MAV_TYPE_VTOL_RESERVED5:
            case mavlink20.MAV_TYPE_VTOL_RESERVED6:
                return "ArduPlane";
            case mavlink20.MAV_TYPE_QUADROTOR:
            case mavlink20.MAV_TYPE_COAXIAL:
            case mavlink20.MAV_TYPE_HELICOPTER:
            case mavlink20.MAV_TYPE_HEXAROTOR:
            case mavlink20.MAV_TYPE_OCTOROTOR:
            case mavlink20.MAV_TYPE_TRICOPTER:
                return "ArduCopter";
            case mavlink20.MAV_TYPE_GROUND_ROVER:
                return "ArduRover";
            case mavlink20.MAV_TYPE_SUBMARINE: 
                return "ArduSub";
            case mavlink20.MAV_TYPE_ANTENNA_TRACKER:
                return "AntennaTracker";
        }
    };

    getAvailableFlightModes() {
        switch (this.mavType) {
            case mavlink20.MAV_TYPE_QUADROTOR:
            case mavlink20.MAV_TYPE_COAXIAL:
            case mavlink20.MAV_TYPE_HELICOPTER:
            case mavlink20.MAV_TYPE_HEXAROTOR:
            case mavlink20.MAV_TYPE_OCTOROTOR:
            case mavlink20.MAV_TYPE_TRICOPTER:
            case mavlink20.MAV_TYPE_DODECAROTOR:
                return COPTER_MODES;
            case mavlink20.MAV_TYPE_FIXED_WING:
            case mavlink20.MAV_TYPE_FLAPPING_WING:
                return PLANE_MODES;
            case mavlink20.MAV_TYPE_GROUND_ROVER:
            case mavlink20.MAV_TYPE_SURFACE_BOAT:
                return ROVER_MODES;
            case mavlink20.MAV_TYPE_SUBMARINE:
                return SUB_MODES;
            case mavlink20.MAV_TYPE_ANTENNA_TRACKER:
                return TRACKER_MODES;
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_DUOROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER_QUADROTOR:
            case mavlink20.MAV_TYPE_VTOL_TILTROTOR:
            case mavlink20.MAV_TYPE_VTOL_FIXEDROTOR:
            case mavlink20.MAV_TYPE_VTOL_TAILSITTER:
            case mavlink20.MAV_TYPE_VTOL_TILTWING:
            case mavlink20.MAV_TYPE_VTOL_RESERVED5:
            case mavlink20.MAV_TYPE_VTOL_RESERVED6:
                return QUADPLANE_MODES;
            default:
                return {};
        }
    };

    setMode(mode) {
        const msg = new mavlink20.messages.set_mode(
            this.mavId, // target_system
            mavlink20.MAV_MODE_FLAG_CUSTOM_MODE_ENABLED, // base_mode
            mode // custom_mode
        );
        this.sendMavlinkMsg(msg);
    };

    setGuidedMode() {
        const GUIDED_MODE_ID = 15;
        this.setMode(GUIDED_MODE_ID);
    };

    goTo(lat, lon, alt, frame) {
        const msg = new mavlink20.messages.mission_item_int(
            this.mavId, // target_system
            this.componentId, // target_component
            0, // seq
            frame, // frame
            mavlink20.MAV_CMD_NAV_WAYPOINT, // command
            2, // current (0=not current, 1=current)
            0, // autocontinue
            0, // param1 (hold time)
            0, // param2 (acceptance radius)
            0, // param3 (pass radius)
            0, // param4 (yaw angle)
            Math.round(lat * 1e7), // x (latitude, scaled to int)
            Math.round(lon * 1e7), // y (longitude, scaled to int)
            alt // z (altitude)
        );
        this.sendMavlinkMsg(msg);
    };

    setHome(lat, lon, alt, frame) {
        const msg = new mavlink20.messages.command_int(
            this.mavId, // target_system
            this.componentId, // target_component
            frame, // frame
            mavlink20.MAV_CMD_DO_SET_HOME, // command
            1, // current (0=not current, 1=current)
            0, // autocontinue
            0, // param1
            0, // param2
            0, // param3
            0, // param4
            Math.round(lat * 1e7), // x (latitude, scaled to int)
            Math.round(lon * 1e7), // y (longitude, scaled to int)
            alt // z (altitude)
        );
        this.sendMavlinkMsg(msg);
    };

    requestParamList() {
        const msg = new mavlink20.messages.param_request_list(
            this.mavId, // target_system
            this.componentId // target_component
        );
        this.sendMavlinkMsg(msg);
    };

    reboot() {
       const msg = new mavlink20.messages.command_long(
            this.mavId, // target_system
            this.componentId, // target_component
            mavlink20.MAV_CMD_PREFLIGHT_REBOOT_SHUTDOWN, // command
            0, // confirmation
            1, // param1
            0, // param2
            0, // param3
            0, // param4
            0, // param5
            0, // param6
            0 // param7
        );
        this.sendMavlinkMsg(msg);
    };

    armDisarm(arm, force = false) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_COMPONENT_ARM_DISARM,
            0,
            arm ? 1 : 0,         // param1: 1=arm, 0=disarm
            force ? 21196 : 0,   // param2: magic number to force
            0, 0, 0, 0, 0,
        );
        this.sendMavlinkMsg(msg);
    };

    setParams(params) {
        Object.keys(params).forEach(param_id => {
            const msg = new mavlink20.messages.param_set(
                this.mavId, // target_system
                this.componentId, // target_component
                param_id, // param_id
                params[param_id].value, // param_value
                params[param_id].type // param_type
            );
            console.log(param_id, params[param_id])
            this.sendMavlinkMsg(msg);
        });
    };

    resetParamsToDefault() {
        console.log('Resetting parameters to default');
        const msg = new mavlink20.messages.command_long(
            this.mavId, // target_system
            this.componentId, // target_component
            mavlink20.PARAM_RESET_ALL_DEFAULT, // command
            0, // confirmation
            0, // param1
            0, // param2
            0, // param3
            0, // param4
            0, // param5
            0, // param6
            0 // param7
        );
        this.sendMavlinkMsg(msg);
    };

    // Trigger PREFLIGHT_CALIBRATION. Pass per-axis params explicitly.
    // ArduPilot bits:
    //   p1: 1=gyro
    //   p3: 1=ground pressure (baro)
    //   p5: 1=accel(full), 2=board level/simple, 4=accel level only
    //   p6: 1=airspeed, 3=compassmot
    preflightCalibration({ gyro = 0, mag = 0, baro = 0, radio = 0, accel = 0, sixth = 0, esc = 0 } = {}) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_PREFLIGHT_CALIBRATION,
            0,
            gyro, mag, baro, radio, accel, sixth, esc,
        );
        this.sendMavlinkMsg(msg);
    };

    calibrateGyro()       { this.preflightCalibration({ gyro: 1 }); };
    calibrateBaro()       { this.preflightCalibration({ baro: 1 }); };
    calibrateAccelFull()  { this.preflightCalibration({ accel: 1 }); };
    calibrateAccelLevel() { this.preflightCalibration({ accel: 4 }); };
    calibrateBoardLevel() { this.preflightCalibration({ accel: 2 }); };
    calibrateAirspeed()   { this.preflightCalibration({ sixth: 1 }); };
    cancelCalibration()   { this.preflightCalibration({}); };

    // Acknowledge a position during interactive accel calibration.
    // pos: 1=LEVEL, 2=LEFT, 3=RIGHT, 4=NOSEDOWN, 5=NOSEUP, 6=BACK
    accelCalVehiclePos(pos) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_ACCELCAL_VEHICLE_POS,
            0,
            pos, 0, 0, 0, 0, 0, 0,
        );
        this.sendMavlinkMsg(msg);
    };

    // Compass / magnetometer calibration.
    //   mask: bitmask of compass instances (0 = all)
    //   retry, autosave: 0/1
    //   delay: seconds before starting
    //   autoreboot: 0/1
    startMagCal({ mask = 0, retry = 1, autosave = 1, delay = 0, autoreboot = 0 } = {}) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_DO_START_MAG_CAL,
            0,
            mask, retry, autosave, delay, autoreboot, 0, 0,
        );
        this.sendMavlinkMsg(msg);
    };

    acceptMagCal(mask = 0) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_DO_ACCEPT_MAG_CAL,
            0,
            mask, 0, 0, 0, 0, 0, 0,
        );
        this.sendMavlinkMsg(msg);
    };

    cancelMagCal(mask = 0) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_DO_CANCEL_MAG_CAL,
            0,
            mask, 0, 0, 0, 0, 0, 0,
        );
        this.sendMavlinkMsg(msg);
    };

    // ---- Motor Test (MAV_CMD_DO_MOTOR_TEST = 209) ----
    // motorNumber:     1..N motor index (per chosen order). 0 = all motors (autopilot dependent).
    // throttleType:    0=percent, 1=PWM, 2=pilot's current throttle (default 0)
    // throttleValue:   percentage 0..100 (or PWM us when throttleType=1)
    // timeoutSec:      seconds the motor should spin
    // motorCount:      number of motors to test in sequence (0/1 for single motor)
    // testOrder:       0=DEFAULT, 1=SEQUENCE, 2=BOARD
    motorTest(motorNumber, throttleValue, timeoutSec = 2, {
        throttleType = 0, motorCount = 0, testOrder = 0,
    } = {}) {
        const msg = new mavlink20.messages.command_long(
            this.mavId,
            this.componentId,
            mavlink20.MAV_CMD_DO_MOTOR_TEST,
            0,
            motorNumber,
            throttleType,
            throttleValue,
            timeoutSec,
            motorCount,
            testOrder,
            0,
        );
        this.sendMavlinkMsg(msg);
    };

    // Stop motor test on a specific motor by setting throttle=0 and timeout=0.
    motorTestStop(motorNumber = 0) {
        this.motorTest(motorNumber, 0, 0);
    };

    // ---- Legacy dataflash log download (LOG_REQUEST_LIST/LOG_DATA flow) ----
    logRequestList(start = 0, end = 0xFFFF) {
        const msg = new mavlink20.messages.log_request_list(
            this.mavId, this.componentId, start, end,
        );
        this.sendMavlinkMsg(msg);
    };

    logRequestData(id, ofs = 0, count = 0xFFFFFFFF) {
        const msg = new mavlink20.messages.log_request_data(
            this.mavId, this.componentId, id, ofs, count,
        );
        this.sendMavlinkMsg(msg);
    };

    logRequestEnd() {
        const msg = new mavlink20.messages.log_request_end(
            this.mavId, this.componentId,
        );
        this.sendMavlinkMsg(msg);
    };

    logErase() {
        const msg = new mavlink20.messages.log_erase(
            this.mavId, this.componentId,
        );
        this.sendMavlinkMsg(msg);
    };
}

module.exports = Vehicle;